import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import { Ticket, TicketAttachment } from '../types/ticket';
import { useForm, UseFormReturn } from 'react-hook-form';
import { getTicketUppyInstance } from '../utils/getTicketAttachmentUppy';
import { attachLicensePlateScan } from '../utils/attachLicensePlateScan';
import { findTickets } from 'api/canpark/tickets/findTickets';
import { canPlatePark } from 'api/canpark/registeredPlates/canPlatePark';
import { getLookupStartDateForParkingLocation } from '../utils/getLookupStartDate';
import { ParkingLocation } from '../types/parkingLocation';
import { VinVehicleInfo } from 'api/canpark/scanning/extractVin';
import {
	StageId,
	StageDefinition,
	buildPipeline,
	getStage,
} from '../flow/stages';
import { getNextViolationTicketType } from '../utils/getNextViolationTicketType';

interface ActiveVehicleContextType {
	showDrawer: boolean;
	stage: StageId;
	// Full plate history (incl. out-of-window tickets, for display).
	tickets: Ticket[];
	// Subset of `tickets` inside the lot's lookup window — the tickets that count
	// toward the violation count/decision. Use this anywhere "records that count".
	inWindowTickets: Ticket[];
	parkingLocation: ParkingLocation | null;
	currentSearch: {
		parkingLocation: ParkingLocation;
		licenseNumber: string;
		licenseState: string;
	} | null;
	ticketFormMethods: UseFormReturn<Ticket, any, undefined>;
	parkingAuthorization: {
		allowed: boolean;
		validFrom?: Date;
		validUntil?: Date;
	} | null;
	uppyTicket: any;
	openModal: boolean;
	setOpenModal: (open: boolean) => void;
	isLoading: boolean;
	vin: string | null;
	vinVehicleInfo: VinVehicleInfo | null;
	scanImage: string | null;
	closeDrawer: () => void;
	setStage: (stage: StageId) => void;
	setCurrentSearch: (search: {
		parkingLocation: ParkingLocation;
		licenseNumber: string;
		licenseState: string;
	}) => void;
	setVin: (vin: string | null) => void;
	setVinVehicleInfo: (info: VinVehicleInfo | null) => void;
	setPendingScanImage: (dataUrl: string | null) => void;

	// Flow pipeline API
	pipeline: StageDefinition[];
	current: StageDefinition;
	currentIndex: number; // -1 when on a branch stage (towInfo)
	canGoBack: boolean;
	goNext: () => void;
	goBack: () => void;
	goTo: (id: StageId) => void;

	// Lifted local state (was inside TicketForm) so closeDrawer can clean up
	isVinCameraOn: boolean;
	setIsVinCameraOn: (on: boolean) => void;
}

const ActiveVehicleContext = createContext<
	ActiveVehicleContextType | undefined
>(undefined);

export const ActiveVehicleProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [stage, setStage] = React.useState<StageId>('action');
	const [currentSearch, setCurrentSearch] = useState<{
		parkingLocation: ParkingLocation;
		licenseNumber: string;
		licenseState: string;
	} | null>(null);
	const [uppyTicket, setUppyTicket] = useState<any>(null);
	const [openModal, setOpenModal] = useState(false);
	const [tickets, setTickets] = useState<Ticket[]>([]);
	const [parkingAuthorization, setParkingAuthorization] = useState<{
		allowed: boolean;
		validFrom?: Date;
		validUntil?: Date;
	} | null>(null);
	const [vin, setVin] = useState<string | null>(null);
	const [vinVehicleInfo, setVinVehicleInfo] = useState<VinVehicleInfo | null>(
		null,
	);
	const [isVinCameraOn, setIsVinCameraOn] = useState(false);
	// Two slots, one setter:
	//   - `pendingScanImageRef` is consumed once by `initializeTicketUppy` to
	//     attach the scan as a ticket file; it nulls itself after attachment.
	//   - `scanImage` drives the persistent header rendered across info →
	//     ticket → confirmation. It outlives the Uppy hand-off and is only
	//     cleared on `closeDrawer`.
	const pendingScanImageRef = useRef<string | null>(null);
	const [scanImage, setScanImage] = useState<string | null>(null);
	const setPendingScanImage = useCallback((dataUrl: string | null) => {
		pendingScanImageRef.current = dataUrl;
		setScanImage(dataUrl);
	}, []);

	const ticketFormMethods = useForm<Ticket>({
		defaultValues: {
			ticketNumber: '',
			// A plain record is the only safe baseline — never pre-arm a warning.
			// The seed below upgrades this when the lot recommends an action.
			ticketType: 'log',
			parkingZone: '',
			parkingLocationId: '',
			date: new Date(),
			licenseNumber: '',
			licenseState: '',
			vehicleInfo: '',
			comments: '',
			issuedBy: '',
			attachments: [],
			vin: undefined,
			isWithinWindow: true,
		},
	});

	// Tickets that fall inside the lookup window — the ones that count toward the
	// violation count/decision. `tickets` itself holds the full history for display.
	const inWindowTickets = useMemo(
		() => tickets.filter((t) => t.isWithinWindow),
		[tickets],
	);

	const parkingLocation = currentSearch?.parkingLocation || null;
	const pipeline = useMemo(
		() => buildPipeline(parkingLocation),
		[parkingLocation],
	);
	const currentIndex = pipeline.findIndex((s) => s.id === stage);
	const current = currentIndex >= 0 ? pipeline[currentIndex] : getStage(stage);
	const canGoBack = currentIndex > 0;

	const goNext = useCallback(() => {
		const idx = pipeline.findIndex((s) => s.id === stage);
		if (idx < 0 || idx >= pipeline.length - 1) return;
		setStage(pipeline[idx + 1].id);
	}, [pipeline, stage]);

	const goBack = useCallback(() => {
		const idx = pipeline.findIndex((s) => s.id === stage);
		if (idx <= 0) return;
		setStage(pipeline[idx - 1].id);
	}, [pipeline, stage]);

	const goTo = useCallback((id: StageId) => {
		setStage(id);
	}, []);

	const initializeTicketUppy = () => {
		const uppy = getTicketUppyInstance();
		uppy.on('complete', (result: any) => {
			const newAttachments: TicketAttachment[] = result.successful.map(
				(file: any) => ({
					AttachmentID: file.response.body.id,
					Name: file.response.body.filename ?? file.name,
					Size: file.response.body.size ?? file.size,
					ContentType: file.response.body.mime_type ?? file.type,
				}),
			);
			const prevAttachments = ticketFormMethods.getValues('attachments');
			ticketFormMethods.setValue('attachments', [
				...newAttachments,
				...prevAttachments,
			]);
		});
		setUppyTicket(uppy);

		const pendingScan = pendingScanImageRef.current;
		if (pendingScan) {
			pendingScanImageRef.current = null;
			attachLicensePlateScan(
				pendingScan,
				uppy,
				ticketFormMethods.getValues('attachments') ?? [],
				(next) => ticketFormMethods.setValue('attachments', next),
			).catch((err) => {
				console.error(
					'[ActiveVehicleProvider] Failed to attach license plate scan:',
					err,
				);
			});
		}
	};

	useEffect(() => {
		if (!currentSearch) return;

		// Land on the first visible stage for this lot. With showActionStage=false
		// the action stage is removed from the pipeline, so the drawer opens on
		// carPhoto (if enabled) or ticket — no flicker, no auto-advance hack.
		const initialStage = buildPipeline(currentSearch.parkingLocation)[0]?.id;
		if (initialStage) setStage(initialStage);

		setIsLoading(true);
		initializeTicketUppy();

		const lookupStartDate = getLookupStartDateForParkingLocation(
			currentSearch.parkingLocation,
		);

		Promise.all([
			canPlatePark(
				currentSearch.licenseNumber,
				currentSearch.licenseState,
				currentSearch.parkingLocation.id,
			).then((info) => {
				setParkingAuthorization(info);
				return info;
			}),

			findTickets(
				currentSearch.licenseNumber,
				currentSearch.licenseState,
				currentSearch.parkingLocation.id,
				lookupStartDate,
			).then((loadedTickets) => {
				setTickets(loadedTickets);
				return loadedTickets;
			}),
		])
			.then(([authInfo, loadedTickets]) => {
				// Seed ticketType from the recommendation when the action stage
				// is hidden — the operator never sees the action UI, so without
				// this seed they'd land on ticket form with the default
				// ('logAndWarn') instead of the lot's recommended action.
				if (
					currentSearch.parkingLocation.showActionStage === false &&
					!authInfo?.allowed
				) {
					const decision = getNextViolationTicketType({
						// Decision is window-only. The util also re-filters by date
						// internally, but pass the in-window subset explicitly for clarity.
						tickets: loadedTickets.filter((t) => t.isWithinWindow),
						parkingLocation: currentSearch.parkingLocation,
					});
					// Upgrade to the recommended action; otherwise (withinLimit /
					// sessionClosed — common on hourly lots) force a plain record
					// so the form never inherits a stale 'logAndWarn' default.
					ticketFormMethods.setValue(
						'ticketType',
						decision.action === 'suggestTicket'
							? decision.ticketType
							: 'log',
					);
				}
			})
			.catch((error) => {
				console.error('Error loading parking data:', error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [currentSearch]);

	const showDrawer = currentSearch ? true : false;
	const closeDrawer = () => {
		setTickets([]);
		setParkingAuthorization(null);
		setStage('action');
		setCurrentSearch(null);
		setVin(null);
		setVinVehicleInfo(null);
		pendingScanImageRef.current = null;
		setScanImage(null);
		setOpenModal(false);
		setIsVinCameraOn(false);
		ticketFormMethods.reset();
		try {
			uppyTicket?.cancelAll?.();
		} catch (err) {
			console.warn('[ActiveVehicleProvider] uppy cancelAll failed:', err);
		}
	};
	return (
		<ActiveVehicleContext.Provider
			value={{
				parkingLocation,
				showDrawer,
				stage,
				tickets,
				inWindowTickets,
				currentSearch,
				ticketFormMethods,
				parkingAuthorization,
				uppyTicket,
				openModal,
				setOpenModal,
				isLoading,
				vin,
				vinVehicleInfo,
				scanImage,
				closeDrawer,
				setCurrentSearch,
				setStage,
				setVin,
				setVinVehicleInfo,
				setPendingScanImage,
				pipeline,
				current,
				currentIndex,
				canGoBack,
				goNext,
				goBack,
				goTo,
				isVinCameraOn,
				setIsVinCameraOn,
			}}
		>
			{children}
		</ActiveVehicleContext.Provider>
	);
};

export const useActiveVehicle = () => {
	const context = useContext(ActiveVehicleContext);
	if (!context)
		throw new Error('useUIContext must be used within a UIProvider');
	return context;
};
